import { useQueryClient } from '@tanstack/react-query';
import { Star } from 'lucide-react';

import { getHistoricoControllerResumoQueryKey } from '@/api/generated/historico/historico';
import {
  getPacientesControllerListarQueryKey,
  getPacientesControllerObterQueryKey,
  usePacientesControllerDesfavoritar,
  usePacientesControllerFavoritar,
} from '@/api/generated/pacientes/pacientes';
import { ApiError } from '@/api/http-client';
import { Button } from '@/shared/ui/button';
import { toast } from '@/shared/ui/sonner';

/**
 * Só o que a estrela precisa, em vez de `PacienteResponse` inteiro: o resumo do
 * histórico traz os mesmos três campos com outro nome de tipo, e exigir a
 * resposta completa obrigaria a tela de lá a inventar um objeto de paciente.
 */
interface Props {
  paciente: { id: string; nome: string; favorito: boolean };
  className?: string;
}

/**
 * Estrela de favoritar (F5-1).
 *
 * ⚠️ **Favorito é de quem está logado, não da clínica.** O servidor devolve
 * `favorito` já resolvido para o usuário da sessão, e as duas rotas só mexem na
 * marcação de quem chamou — desmarcar aqui não tira o paciente da lista do
 * colega no mesmo ambiente.
 *
 * `aria-pressed` em vez de dois `aria-label` diferentes: é um botão de duas
 * posições, e o leitor de tela precisa anunciar o ESTADO, não só a ação.
 */
export function BotaoFavorito({ paciente, className }: Props) {
  const queryClient = useQueryClient();
  const favoritar = usePacientesControllerFavoritar();
  const desfavoritar = usePacientesControllerDesfavoritar();

  const emCurso = favoritar.isPending || desfavoritar.isPending;

  async function alternar() {
    const acao = paciente.favorito ? desfavoritar : favoritar;

    try {
      await acao.mutateAsync({ id: paciente.id });
      // Invalida a listagem inteira (todas as combinações de filtro) porque o
      // filtro "só favoritos" muda QUAIS linhas existem, não só a estrela de
      // uma delas.
      await queryClient.invalidateQueries({ queryKey: getPacientesControllerListarQueryKey() });
      await queryClient.invalidateQueries({
        queryKey: getPacientesControllerObterQueryKey(paciente.id),
      });
      // O resumo do histórico carrega a mesma estrela no cabeçalho. Sem isto,
      // marcar na lista e navegar para o dossiê mostraria o estado anterior.
      await queryClient.invalidateQueries({
        queryKey: getHistoricoControllerResumoQueryKey(paciente.id),
      });
    } catch (erro) {
      toast.error(
        erro instanceof ApiError ? erro.message : 'Não foi possível atualizar o favorito.',
      );
    }
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className={className}
      disabled={emCurso}
      aria-pressed={paciente.favorito}
      aria-label={`Favoritar ${paciente.nome}`}
      title={paciente.favorito ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
      onClick={() => void alternar()}
    >
      {/* `fill` só quando marcado: a estrela vazada é o estado "dá para
          marcar", e a cheia é a que se acha varrendo a lista de longe. */}
      <Star
        className={`w-4 h-4 ${paciente.favorito ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground'}`}
      />
    </Button>
  );
}
